import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addTemplate, deleteTemplate, getTemplate, updateTemplate } from "../services/TemplateServices";
import Truncate from "../utils/Truncate";
import useAuthStore from "../store/useAuthStore";
import Modal from "../component/Modal";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import {
  createEmptyTemplateDelta,
  getTemplateEditorValue,
  isTemplateBodyEmpty,
  serializeTemplateDelta,
} from "../utils/templateDelta";
import { ReactComponent as DeleteIcon } from "../assets/delete.svg";

const QUILL_MODULES = {
  toolbar: [["bold", "italic", "underline"], ["link"], ["clean"]],
};

const QUILL_FORMATS = ["bold", "italic", "underline", "link"];

const EditTemplatePage = () => {
  const { role, token } = useAuthStore((state) => state);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mode, setMode] = useState("view"); // "add" | "edit" | "view"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const quillRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/");
    } else {
      allTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const allTemplates = async () => {
    const res = await getTemplate();
    setTemplateList(res?.data?.data || []);
  };

  const openTemplateModal = (template, nextMode) => {
    setSelectedTemplate({
      ...template,
      editorValue: getTemplateEditorValue(template.body),
    });
    setMode(nextMode);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const editor = quillRef.current?.getEditor();
      const serializedBody = editor
        ? serializeTemplateDelta(editor.getContents())
        : selectedTemplate.body;

      if (isTemplateBodyEmpty(serializedBody)) {
        toast.error("Template body is required");
        return;
      }

      const payload = {
        ...selectedTemplate,
        body: serializedBody,
      };
      delete payload.editorValue;

      let res;
      if (mode === "edit") {
        res = await updateTemplate(payload);
      } else if (mode === "add") {
        if (payload.isGlobal && !payload.fromEmail) {
          toast.error("Please add From email");
          return;
        }
        res = await addTemplate(payload);
      }

      if (res.status === 200) {
        toast.success(
          mode === "edit"
            ? "Template updated successfully!"
            : "Template added successfully!"
        );
        setIsModalOpen(false);
        allTemplates();
        setSelectedTemplate(null);
      }
    } catch (err) {
      console.error("Error saving template:", err);
      toast.error("Something went wrong");
    }
  };

  // if (templateList.length === 0) {
  //   return <div>No Template Found!!!</div>
  // }

  const handleDelete = async (id) => {
    try {
      const res = await deleteTemplate(id)
      console.log(res)
      if(res.status===200){
        toast.success("Template deleted Successfully")
        allTemplates()
      }
    } catch (error) {
      toast.error("Something went wrong")
    }
  }

  return (
    <div className="w-full">
      <h1 className="text-xl w-8/12 mx-auto font-bold mb-4 text-center">Email Templates</h1>

      <div className="overflow-y-auto" style={{ maxHeight: "70vh", maxWidth: "100vw" }}>
        {templateList.map((item) => (
          <TemplateCard
            key={item.id}
            id={item.id}
            name={item.name}
            subject={item.subject}
            onEdit={() => openTemplateModal(item, "edit")}
            onView={() => openTemplateModal(item, "view")}
            isDefault={item.isDefault}
            isGlobal={item.isGlobal}
            role={role}
            onDelete={() => handleDelete(item.id)}   
          />
        ))}
      </div>
      {
        role !== "user" &&
        <div className="text-center">
          <button
            onClick={() => {
              setSelectedTemplate({
                name: "",
                subject: "",
                body: serializeTemplateDelta(createEmptyTemplateDelta()),
                editorValue: createEmptyTemplateDelta(),
                isDefault: false,
                fromEmail: ""
              });
              setMode("add");
              setIsModalOpen(true);
            }}
            className="mt-4 bg-green-500 text-white py-2 px-4 rounded-md"
          >
            Add Template
          </button>
        </div>
      }

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {selectedTemplate && (
          <div className="max-h-[80vh] overflow-y-auto p-4">
            <h2 className="text-lg font-bold mb-4">
              {mode === "edit"
                ? "Edit Template"
                : mode === "add"
                  ? "Add Template"
                  : "View Template"}
            </h2>

            {/* Name */}
            <label className="block text-sm font-medium mb-1">Template Name</label>
            <input
              type="text"
              value={selectedTemplate.name}
              onChange={(e) =>
                setSelectedTemplate((currentTemplate) => ({
                  ...currentTemplate,
                  name: e.target.value,
                }))
              }
              disabled={mode === "view"}
              className="w-full p-2 border mb-4 rounded-md"
              placeholder="Template Name"
            />

            {/* Subject */}
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              value={selectedTemplate.subject}
              onChange={(e) =>
                setSelectedTemplate((currentTemplate) => ({
                  ...currentTemplate,
                  subject: e.target.value,
                }))
              }
              disabled={mode === "view"}
              className="w-full p-2 border mb-4 rounded-md"
              placeholder="Subject"
            />

            {
              (role !== "user" && role !== "sub-admin") && (
                <>
                  <label className="block text-sm font-medium mb-1">From Email</label>
                  <input
                    type="email"
                    value={selectedTemplate.fromEmail}
                    onChange={(e) =>
                      setSelectedTemplate((currentTemplate) => ({
                        ...currentTemplate,
                        fromEmail: e.target.value,
                      }))
                    }
                    disabled={mode === "view"}
                    className="w-full p-2 border mb-4 rounded-md"
                    placeholder="Subject"
                  />
                </>
              )
            }

            {/* Body */}
            <label className="block text-sm font-medium mb-1">Body</label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              value={selectedTemplate.editorValue ?? selectedTemplate.body}
              onChange={(_, __, ___, editor) =>
                setSelectedTemplate((currentTemplate) => ({
                  ...currentTemplate,
                  body: serializeTemplateDelta(editor.getContents()),
                  editorValue: editor.getContents(),
                }))
              }
              readOnly={mode === "view"}
            />


            {/* isDefault */}
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                checked={selectedTemplate.isDefault}
                onChange={(e) =>
                  setSelectedTemplate((currentTemplate) => ({
                    ...currentTemplate,
                    isDefault: e.target.checked,
                  }))
                }
                disabled={mode === "view"}
                className="mr-2"
              />
              <label className="text-sm">Set as Default</label>
            </div>

            {/* isGlobal */}
            {
              role === "admin" &&
              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  checked={selectedTemplate.isGlobal}
                  onChange={(e) =>
                    setSelectedTemplate((currentTemplate) => ({
                      ...currentTemplate,
                      isGlobal: e.target.checked,
                    }))
                  }
                  disabled={mode === "view"}
                  className="mr-2"
                />
                <label className="text-sm">Set as Global</label>
              </div>
            }

            {/* Save Button */}
            {(mode === "edit" || mode === "add") && (
              <button
                onClick={handleSave}
                className="mt-4 bg-blue-500 text-white py-2 px-4 rounded-md"
              >
                Save
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EditTemplatePage;

const TemplateCard = ({ id, name, subject, onEdit, onView, isDefault, isGlobal, role, onDelete }) => {
  const canEdit =
    role === "admin" ||               // admin can edit everything
    (role === "sub-admin" && !isGlobal); // sub-admin can edit only non-global
  return (
    <div
      className={`w-8/12 m-2 mx-auto p-4 shadow-md rounded-md ${isDefault ? "bg-gray-200 border border-gray-400" : "bg-white"
        }`}
    >
      <div className="flex justify-between align-middle">
        <div>
          <Truncate text={name} maxLength={10} />
        </div>
        <div>
          <Truncate text={subject} maxLength={15} />
        </div>
      </div>
      <div className="flex justify-end">
        {
          role === "admin" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete()
              }}
              className="bg-red-400 text-white m-2 rounded-md px-3 py-1"
            >
              <DeleteIcon className="h-5 w-7" />
            </button>
          )
        }
        {
          canEdit && (
            <button
              onClick={onEdit}
              className="bg-blue-500 text-white m-2 rounded-md px-3 py-1"
            >
              Edit
            </button>
          )}
        <button
          onClick={onView}
          className="bg-gray-500 text-white m-2 rounded-md px-3 py-1"
        >
          View
        </button>
      </div>
    </div>
  );
};
